import React, { useState } from 'react';
import { Calendar, Package, Zap, Check, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardHeader, CardContent, CardFooter } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';

export const Preorder = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    productInterest: '',
    quantity: 1,
    preferredDate: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Preorder submitted:', formData);
    toast({
      title: "Preorder Received!",
      description: "We'll contact you within 24 hours to confirm your order.",
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      productInterest: '',
      quantity: 1,
      preferredDate: '',
      message: ''
    });
  };

  const preorderCategories = [
    {
      id: 'juice-packs',
      name: 'Juice Packs',
      description: 'Custom juice bundles for events, offices, or personal use',
      minOrder: '12 bottles',
      leadTime: '48 hours'
    },
    {
      id: 'shot-bundles',
      name: 'Shot Bundles',
      description: 'Energy shot packs perfect for morning routines or group orders',
      minOrder: '24 shots',
      leadTime: '48 hours'
    },
    {
      id: 'mixed-drink-kits',
      name: 'Mixed Drink Kits',
      description: 'Event-ready mixers for parties, gatherings, and celebrations',
      minOrder: '6 kits',
      leadTime: '72 hours'
    },
    {
      id: 'tee-drops',
      name: 'Limited Tee Drops',
      description: 'Reserve upcoming streetwear releases before they sell out',
      minOrder: '1 item',
      leadTime: '1-2 weeks'
    },
    {
      id: 'catering',
      name: 'Event Catering',
      description: 'Full beverage service for corporate events, parties, and festivals',
      minOrder: '50 servings',
      leadTime: '1 week'
    }
  ];

  const benefits = [
    'Lock in current pricing',
    'Priority fulfillment',
    'Custom flavor combinations',
    'Flexible delivery scheduling',
    'Bulk order discounts',
    'Dedicated support team'
  ];

  return (
    <section id="preorder" className="py-24 bg-gradient-to-b from-zinc-900 to-black">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="bg-amber-400/10 text-amber-400 border-amber-400/20 mb-4">
            <Calendar className="w-3 h-3 mr-1" />
            Preorder Now
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
            Reserve Your <span className="text-amber-400">Order</span>
          </h2>
          <p className="text-xl text-gray-300">
            Planning an event? Need bulk orders? Want first access to drops?
            <br />
            Secure your products ahead of time with our preorder system.
          </p>
        </div>

        {/* Benefits Bar */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-lime-400 flex-shrink-0" />
                <span className="text-gray-300 text-sm">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Preorder Categories */}
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">What Can You Preorder?</h3>
            </div>

            <div className="space-y-4">
              {preorderCategories.map((category) => (
                <Card
                  key={category.id}
                  className="bg-zinc-900 border-zinc-800 hover:border-amber-400/50 transition-all duration-300 group cursor-pointer"
                  onClick={() => setFormData({ ...formData, productInterest: category.name })}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="bg-amber-400/10 p-2 rounded-lg group-hover:bg-amber-400/20 transition-colors">
                          <Package className="w-5 h-5 text-amber-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white group-hover:text-amber-400 transition-colors">
                          {category.name}
                        </h4>
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm mb-4">{category.description}</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Min: {category.minOrder}</span>
                      <Badge variant="outline" className="text-lime-400 border-lime-400/30">
                        {category.leadTime} lead time
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Important Notice */}
            <Card className="bg-gradient-to-br from-orange-400/10 to-zinc-900 border-orange-400/20">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white font-semibold mb-2">
                      Orlando & Daytona Area Priority
                    </p>
                    <p className="text-sm text-gray-300">
                      Local customers get expedited service and delivery options. 
                      Shipping available nationwide for select products.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Preorder Form */}
          <Card className="bg-zinc-900 border-zinc-800 h-fit sticky top-24">
            <CardHeader>
              <h3 className="text-2xl font-bold text-white">Submit Your Preorder</h3>
              <p className="text-gray-400 text-sm">
                Fill out the form and we'll get back to you with pricing and availability
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-amber-400"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-amber-400"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="775-383-9345"
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-amber-400"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="productInterest" className="text-white">Product Interest *</Label>
                  <select
                    id="productInterest"
                    name="productInterest"
                    value={formData.productInterest}
                    onChange={handleChange}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400"
                  >
                    <option value="">Select a category</option>
                    {preorderCategories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quantity" className="text-white">Quantity</Label>
                    <Input
                      id="quantity"
                      name="quantity"
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={handleChange}
                      className="bg-zinc-800 border-zinc-700 text-white focus:border-amber-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="preferredDate" className="text-white">Preferred Date</Label>
                    <Input
                      id="preferredDate"
                      name="preferredDate"
                      type="date"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      className="bg-zinc-800 border-zinc-700 text-white focus:border-amber-400"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-white">Additional Details</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your event, flavor preferences, delivery needs, etc."
                    rows={4}
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-amber-400 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-amber-400 hover:bg-amber-500 text-black font-bold text-lg py-6"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Submit Preorder Request
                </Button>

                <div className="space-y-3">
                  <p className="text-xs text-gray-500 text-center">
                    We'll review your request and contact you within 24 hours with
                    pricing, availability, and next steps.
                  </p>
                  
                  <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-3">
                    <p className="text-xs text-gray-400 text-center leading-relaxed">
                      <span className="font-semibold text-red-400">⚠️ AGE RESTRICTION:</span> Orders containing 
                      mixed drinks require recipient to be 21+ years of age. Valid ID required at delivery. 
                      By submitting, you confirm legal drinking age compliance.
                    </p>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <Card className="bg-gradient-to-r from-amber-400/10 via-lime-400/10 to-amber-400/10 border-amber-400/20 max-w-2xl mx-auto">
            <CardContent className="pt-8 pb-8">
              <Zap className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">
                Need It Sooner?
              </h3>
              <p className="text-gray-300 mb-4">
                For same-day or next-day orders in Orlando, call or text us directly
              </p>
              <a 
                href="tel:775-383-9345"
                className="inline-block"
              >
                <Button size="lg" className="bg-white hover:bg-amber-400 text-black font-bold">
                  Call: 775-383-9345
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
